import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { fetchBookingDetails } from '../../action/bookingsCommands';
import { IssueReport } from '../../api/types';
import { makeDepotDetailsUrl } from '../../routes';
import Permission from '../../common/permissions';
import { makePermissionListSelector, tenantSelector } from '../../selector/authenticationSelector';
import { makeBookingMultipleSelector, makeBookingSelector } from '../../selector/bookingsSelector';
import { makePermittedDepotListSelector } from '../../selector/depotsSelector';
import { makePermittedIssueReportListSelector } from '../../selector/issueReportsSelector';
import { makePermittedLocationListSelector } from '../../selector/locationsSelector';
import EditIssueReportDialog from './EditIssueReportDialog';
import DeleteIssueReportButton from './DeleteIssueReportButton';
import { store } from '../../store/store';
import Command from '../../action/command';
import { useUpdateIssueReportMutation } from '../../api/issueReport/issueReportApiSlice';
import { useAuth0 } from '../../react-auth0-spa';
import CustomButton from '../../components/CustomButton';
import IssueReportStatusChip from '../issueReports/IssueReportStatusChip';
import Logger from '../../common/logger';
import { getIssueTypeText } from '../issueReports/util/issueReportHelpers';
import { useGetLocationListQuery } from '../../api/locations/locationsApiSlice';
import { normalizeBookingNumber } from '../issueReports/util/issueReportBookingMatch';
import { isRoomFamily } from '../../utils/useCases';
import { roomNounOf } from '../../utils/useCaseLabels';

interface IssueReportInfoProps {
    issueReport: IssueReport;
}

const IssueReportInfo = (props: IssueReportInfoProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const permission = useSelector(makePermissionListSelector());
    const [editIssueReportDialogOpen, setEditIssueReportDialogOpen] = useState<boolean>(false);
    const [addCommentDialogOpen, setAddCommentDialogOpen] = useState(false);
    const [commentText, setCommentText] = useState('');
    const params = useParams<{ issueReportId: string }>();
    const issueReportId = params.issueReportId ?? '';
    const dispatch = useDispatch();
    const tenant = tenantSelector(store.getState());

    const { user } = useAuth0();
    const [updateIssueReport, { isLoading: isUpdating }] = useUpdateIssueReportMutation();

    const permittedIssueReports = useSelector(makePermittedIssueReportListSelector());
    const permittedLocations = useSelector(makePermittedLocationListSelector());
    const permittedDepots = useSelector(makePermittedDepotListSelector());
    const { data: locations } = useGetLocationListQuery(undefined);

    const issueReport = permittedIssueReports.find((issueReport) => issueReport.id === issueReportId);

    let order;
    order = useSelector(makeBookingSelector(normalizeBookingNumber(issueReport?.bookingNumber) || issueReport?.orderId || ''));
    const orders = useSelector(
        makeBookingMultipleSelector([normalizeBookingNumber(issueReport?.bookingNumber) || issueReport?.orderId || ''], 'bookingNumber', tenant),
    );
    if (orders) {
        order = orders && orders.length > 0 ? orders[0] : undefined;
    }

    useEffect(() => {
        if (issueReport?.bookingNumber) {
            const bookingWithoutLetter = normalizeBookingNumber(issueReport.bookingNumber);

            const bookingPattern = /^[A-Z]-\d{8}$/;

            if (bookingPattern.test(bookingWithoutLetter)) {
                dispatch(fetchBookingDetails({ bookingId: bookingWithoutLetter }));
            }
        }
    }, []);

    if (!issueReport) {
        return null;
    }

    let depot: any;
    if (permittedDepots.length !== 0 && issueReport.depotId) {
        depot = permittedDepots.find((depot) => depot.id === issueReport.depotId);
    }

    let location: any;
    if (permittedLocations.length !== 0 && depot && depot.locationId) {
        location = permittedLocations.find((location) => location.id === depot.locationId);
    } else if (locations && locations.length > 0 && issueReport.locationId) {
        location = locations.find((location: { id: string }) => location.id === issueReport.locationId);
    }

    const canEditIssueReports = permission.includes(Permission.editIssueReports());
    Logger('IssueReport').log(issueReport);
    const defaultCardInfo = {
        // Reports filed without a locker (payment issues, app problems, …)
        // must not render "Locker: null" — title them generically instead.
        title: issueReport.door != null
            ? `${t('issue_report.locker')}: ${issueReport.door} - ${t('issue_report.size')}: ${issueReport.doorSize}`
            : t('issue_report.general_issue_title'),
        subTitle1: location ? `${t('issue_report.location')}: ${location.name}` : '',
        subTitle2: `${t('issue_report.issue_type')}: ${getIssueTypeText(issueReport.issueType, t)}`,
        subTitle3: `${t('issue_report.service')}: ${t(`service_type_selector.${issueReport.service}`)}`,
        subTitle4:
            issueReport.door && issueReport.doorSize
                ? `${t('issue_report.locker')}: ${issueReport.door} - ${t('issue_report.size')}: ${issueReport.doorSize}`
                : '',
    };

    // The room noun of the report's use case (venue, apartment, hall).
    const roomNounKey = `issue_report.${roomNounOf(issueReport.service)}`;
    const neulandCardInfo = {
        title: `${t(roomNounKey)}: ${issueReport.doorSize}`,
        subTitle1: `${t('issue_report.address')}: ${issueReport.address}`,
        subTitle2: `${t('issue_report.issue_type')}: ${getIssueTypeText(issueReport.issueType, t)}`,
        subTitle3: `${t('issue_report.service')}: ${t(`service_type_selector.${issueReport.service}`)}`,
    };

    // A room (the room family) gets the room card; everything else the station card.
    const cardInfo: Record<string, string> = isRoomFamily(issueReport.service) ? neulandCardInfo : defaultCardInfo;

    // Reporter email for customer-filed reports: the booking's customer email
    // when the report resolved to an order, else the stored creator email.
    const reporterEmail = order?.contactData?.email || issueReport.userCreatorEmail || '';

    // Creator identity for admin-filed reports: the individual email of whoever
    // created it (your address, a colleague's, or a shared call-center mailbox
    // when that's the account used) — never a generic label.
    const creatorName = issueReport.profileInfo?.name || issueReport.userCreatorName || '';
    const creatorEmail = issueReport.profileInfo?.email || issueReport.userCreatorEmail || '';

    return (
        <div className={'bg-white rounded-xl shadow-sm w-full max-w-2xl mx-auto p-6 text-left'}>
            <header className={'flex flex-row items-center justify-between gap-2 mb-2'}>
                <h3 className={'truncate text-[1.3rem] font-bold flex-1 leading-tight'}>
                    {issueReport.createdBy === 'admin' ? cardInfo?.title : t('issue_reports.general_issue_report_title')}
                </h3>
                {/* SUP equipment check (#1604): customer-flow lifecycle badge,
                    same semantics as on the list card. */}
                {issueReport.status && (
                    <span className={'inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-500 whitespace-nowrap'}>
                        {t(`issue_reports.customer_status.${issueReport.status}`)}
                    </span>
                )}
                <IssueReportStatusChip label={issueReport.state} />
            </header>
            <section className={'text-base flex flex-col gap-2 mb-2'}>
                {order && order?.bookingNumber && (
                    <p className={'text-gray-900'}>
                        <span className={'font-normal'}>{t('issue_report.booking_number')} : </span>
                        {issueReport?.bookingNumber ?? order?.bookingNumber}
                    </p>
                )}

                <p className={'text-gray-900'}>
                    <span className={'font-normal'}>{t('issue_report.created_at')}: </span>
                    <span className={'font-bold'}>{moment(issueReport.createdAt).format('ll LT')}</span>
                </p>
                {issueReport.createdBy === 'user' && (
                    <>
                        {order && order.contactData && order.contactData.firstname && order.contactData.lastname ? (
                            <p className={'text-foreground'}>
                                <span className={'font-normal'}>{t('issue_report.full_name')}: </span>
                                {order.contactData?.firstname} {order.contactData?.lastname}
                            </p>
                        ) : issueReport?.userCreatorName ? (
                            <p className={'text-foreground'}>
                                <span className={'font-normal'}>{t('issue_report.full_name')}: </span>
                                {issueReport.userCreatorName}
                            </p>
                        ) : null}
                        {reporterEmail && (
                            <p className={'text-foreground'}>
                                <span className={'font-normal'}>{t('issue_report.email')}: </span>
                                {reporterEmail}
                            </p>
                        )}
                    </>
                )}
                {issueReport.createdBy === 'admin' && creatorEmail && (
                    <p className={'text-foreground'}>
                        <span className={'font-normal'}>{t('issue_report.created_by')}: </span>
                        {creatorName ? `${creatorName} (${creatorEmail})` : creatorEmail}
                    </p>
                )}
                {cardInfo?.subTitle1 && <p className={'text-foreground'}>{cardInfo?.subTitle1}</p>}
                {cardInfo?.subTitle2 && <p className={'text-foreground'}>{cardInfo?.subTitle2}</p>}
                {issueReport.createdBy === 'admin' && cardInfo?.subTitle3 && <p className={'text-foreground'}>{cardInfo?.subTitle3}</p>}
                {issueReport.createdBy === 'user' && issueReport.door && cardInfo?.subTitle4 && <p className={'text-foreground'}>{cardInfo?.subTitle4}</p>}
            </section>
            <div className={'flex flex-wrap items-center gap-2'}>
                {canEditIssueReports && (
                    <CustomButton
                        title={t('issue_reports.update_button')}
                        onClick={() => setEditIssueReportDialogOpen(true)}
                        isFilled={true}
                        fullWidth={false}
                    />
                )}
                {/* Straight to the locker this report is about. Taking it out
                    of service happens there and not here: blocking needs the
                    door's current state — in use, until when, already blocked —
                    and the depot door view is the one place that shows it and
                    owns block / unblock. See
                    docs/plans/blocking-lockers-in-use.md.
                    Absent on reports without a locker (payment problems,
                    general questions), where door and depot are null. */}
                {issueReport.depotId && issueReport.door != null && (
                    <CustomButton
                        title={t('issue_report.go_to_locker')}
                        onClick={() => navigate(
                            `${makeDepotDetailsUrl(issueReport.depotId)}?door=${issueReport.door}`,
                        )}
                        isFilled={false}
                        fullWidth={false}
                    />
                )}
                <DeleteIssueReportButton issueReport={issueReport} />
            </div>
            <EditIssueReportDialog
                open={editIssueReportDialogOpen}
                onClose={() => setEditIssueReportDialogOpen(false)}
                issueReport={issueReport}
                onUpdated={() => {
                    dispatch(Command.IssueReport.fetchIssueReports({ includeArchived: true }));
                }}
            />
            {/* Add Comment Modal */}
            {addCommentDialogOpen && (
                <div className={'fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40'}>
                    <div className={'bg-white rounded-xl shadow-lg w-full max-w-sm mx-4 p-6'}>
                        <h3 className={'text-lg font-bold mb-3'}>{t('issue_reports.add_a_comment')}</h3>
                        <textarea
                            className={`w-full p-3 border border-gray-300 rounded-lg
                                 resize-none focus:outline-none focus:ring-2 focus:ring-primary
                                 text-base placeholder-gray-400 mb-4`}
                            rows={4}
                            placeholder={t('issue_reports.comment_placeholder')}
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                        />
                        <div className={'flex justify-end gap-3'}>
                            <button
                                type={'button'}
                                className={
                                    'px-6 py-2 rounded-lg border border-primary text-black font-semibold text-base bg-white hover:bg-primary/10 transition'
                                }
                                onClick={() => {
                                    setAddCommentDialogOpen(false);
                                    setCommentText('');
                                }}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type={'button'}
                                className={'px-6 py-2 rounded-lg bg-primary text-white font-semibold text-base hover:bg-primary/90 transition'}
                                disabled={!commentText.trim() || isUpdating}
                                onClick={async () => {
                                    if (!user || !commentText.trim()) return;
                                    await updateIssueReport({
                                        id: issueReport.id,
                                        comment: commentText,
                                        state: issueReport.state,
                                        profileInfo: {
                                            name: user.name,
                                            sub: user.sub,
                                            nickname: user.nickname,
                                            email: user.email,
                                            picture: user.picture,
                                        },
                                    });
                                    setCommentText('');
                                    setAddCommentDialogOpen(false);
                                    dispatch(Command.IssueReport.fetchIssueReports({ includeArchived: true }));
                                }}
                            >
                                {isUpdating ? t('common.loading') || 'Saving...' : t('common.save') || 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IssueReportInfo;
